import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
  Rectangle,
  CartesianGrid,
} from 'recharts'

export default function AgeBarChart({
  data,
}: Readonly<{
  data: { ageRange: string; users: number }[]
}>) {
  const maxValue = Math.max(...data.map(d => d.users), 0)

  const calculateTicks = (max: number): number[] => {
    if (max === 0) return [0, 100, 200, 300, 400, 500]
    if (max <= 100) return [0, 20, 40, 60, 80, 100]

    const roundedMax = Math.ceil(max / 100) * 100

    const step = Math.max(100, Math.ceil(roundedMax / 6 / 100) * 100)

    const ticks: number[] = []

    for (let i = 0; i <= roundedMax && ticks.length < 20; i += step) {
      ticks.push(i)
    }

    if (ticks.length > 0 && ticks[ticks.length - 1] < roundedMax) {
      ticks.push(roundedMax)
    }

    return ticks
  }

  const dynamicTicks = calculateTicks(maxValue)
  const yAxisMax = dynamicTicks[dynamicTicks.length - 1] || 1200

  // Transform data for Recharts
  const chartData = data.map(item => ({
    ...item,
    name: item.ageRange,
    value: item.users,
  }))

  return (
    <div className="space-y-4">
      {/* Chart Container */}
      <div className="relative h-80 w-full mb-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 40,
              right: 20,
              left: 40,
              bottom: 40,
            }}
            barCategoryGap="20%"
          >
            {/* Diagonal pattern definition */}
            <defs>
              <pattern
                id="diagonalLines"
                patternUnits="userSpaceOnUse"
                width="8"
                height="8"
                patternTransform="rotate(4)"
                radius={12}
              >
                <rect width="8" height="8" fill="#f3f4f6" />
                <line
                  x1="0"
                  y1="8"
                  x2="8"
                  y2="0"
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            {/* Y axis grid lines */}
            <CartesianGrid
              vertical={false}
              stroke="#e5e7eb"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              className="text-xs font-medium fill-ss-neutral-600"
              dy={8}
            />
            <YAxis
              domain={[0, yAxisMax]}
              ticks={dynamicTicks}
              axisLine={false}
              tickLine={false}
              className="text-xs font-medium fill-ss-neutral-600"
              width={40}
              dx={-5}
            />
            {/* <Tooltip content={<CustomTooltip />} /> */}
            <Bar
              className="hover:bg-transparent"
              dataKey="value"
              radius={[12, 12, 12, 12]}
              maxBarSize={36}
              background={{ fill: 'url(#diagonalLines)' }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              shape={(props: any) => (
                <Rectangle {...props} radius={[12, 12, 12, 12]} />
              )}
            >
              <LabelList
                dataKey="value"
                position="top"
                className="text-[10px] font-semibold fill-ss-neutral-950"
                offset={10}
              />
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.value === maxValue ? '#00afe9' : '#b9e6fe'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* X-axis label */}
      <div className="text-center">
        <p className="text-[10px] font-semibold text-ss-neutral-950 leading-[17px]">
          Audience Age
        </p>
      </div>
    </div>
  )
}
